/**
 * Gross vs. net revenue split for the admin Analytics & Reporting hero (COL-422).
 *
 * Every paid rent row used to count as "Rent revenue", so the hero read like
 * CoLiviq earned the full rent of every tenant on the platform — including rent
 * collected on behalf of third-party owners and wired onward. This module
 * classifies each paid rent row by WHO the rent belongs to, so the dashboard
 * can show CoLiviq's own earnings (net) next to the total inflow (gross).
 *
 * Ownership follows the invoice generator (`generate_management_invoices`):
 * a room belongs to its apartment's unit, or to the property when it has no
 * apartment, and the unit's effective lessor is
 *   apartment.lessor_id → lessor behind apartment.owner_user_id → property.lessor_id
 * (see effectiveApartmentOwner). The unit is CoLiviq's own when that lessor is
 * the one flagged `lessors.is_platform`, or when it resolves to no lessor at
 * all — nobody is invoiced or paid out for such a unit, so the rent stays with
 * CoLiviq. Every other unit belongs to a third-party owner.
 *
 * Third-party rent then splits by collection mode, again like the generator:
 *   - company_collects: the tenant pays CoLiviq, which keeps its fee and wires
 *     the rest to the owner. The rent is CoLiviq INFLOW (gross), the fee is
 *     CoLiviq REVENUE (net), the remainder is passed on.
 *   - owner_collects: the tenant pays the owner directly. Such rent never
 *     touches a CoLiviq account, so it is neither inflow nor revenue here —
 *     CoLiviq's earnings from those units arrive as paid management invoices.
 *
 * The retained fee is computed per unit per CALENDAR MONTH with the same
 * formula the generator bills (unitMonthlyRevenue), from the rent that
 * actually came in — the LIVE reading of computeApartmentEconomics. It is
 * always derived from the unit's FULL month, then allocated proportionally
 * into whatever narrower display window (today/week/a custom range) is
 * requested — never re-derived from a partial month's rent, which would
 * reapply the whole month's master-rent deduction (or flat fee) to each
 * sub-window independently and make weekly figures not sum to the month's.
 */
import {
  effectiveApartmentOwner,
  resolveUnitFeeConfig,
  unitMonthlyRevenue,
  type UnitFeeConfig,
} from "@/lib/owner-metrics";
import type { Json } from "@/lib/supabase-types";
import type { CollectionMode, RevenueModel } from "@/types/property";

export type RentOwnership = "coliviq" | "third_party";

/** One billable unit (apartment, or a property's unassigned rooms). */
export type RentUnit = {
  /** `apt:<id>` or `prop:<id>` — the generator's invoice granularity. */
  key: string;
  ownership: RentOwnership;
  collectionMode: CollectionMode;
  feeConfig: UnitFeeConfig;
};

type Id = number | string;

export type RentUnitRoom = {
  id: Id;
  property_id: Id | null | undefined;
  apartment_id?: Id | null;
};

export type RentUnitApartment = {
  id: Id;
  property_id: Id | null | undefined;
  lessor_id: string | null | undefined;
  owner_user_id?: string | null;
  collection_mode: CollectionMode | string | null | undefined;
  revenue_model: RevenueModel | string | null | undefined;
  coliviq_fee_pct: number | string | null | undefined;
  coliviq_fee_amount: number | string | null | undefined;
  master_rent: number | string | null | undefined;
};

export type RentUnitProperty = {
  id: Id;
  lessor_id: string | null | undefined;
  collection_mode: CollectionMode | string | null | undefined;
  revenue_model: RevenueModel | string | null | undefined;
  coliviq_fee_pct: number | string | null | undefined;
  coliviq_fee_amount: number | string | null | undefined;
  master_rent: number | string | null | undefined;
};

const num = (v: number | string | null | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const asMode = (
  v: CollectionMode | string | null | undefined
): CollectionMode | null =>
  v === "company_collects" || v === "owner_collects" ? v : null;

const asModel = (
  v: RevenueModel | string | null | undefined
): RevenueModel | null =>
  v === "full_rent" || v === "pct_of_rent" || v === "flat_fee_per_room"
    ? v
    : null;

/**
 * Resolve every room to its billable unit, ownership and fee config.
 *
 * `platformLessorId` is the lessor flagged is_platform (null when none is —
 * then every lessor-linked unit is third-party, which is the honest reading:
 * nothing says which lessor is us).
 */
export function resolveRentUnits(
  rooms: readonly RentUnitRoom[],
  apartments: readonly RentUnitApartment[],
  properties: readonly RentUnitProperty[],
  lessorByOwnerUser: Readonly<Record<string, string>>,
  platformLessorId: string | null | undefined
): Map<string, RentUnit> {
  const propById = new Map(properties.map((p) => [String(p.id), p]));
  const aptById = new Map(apartments.map((a) => [String(a.id), a]));

  const ownershipFor = (lessorId: string | null): RentOwnership =>
    lessorId == null || (!!platformLessorId && lessorId === platformLessorId)
      ? "coliviq"
      : "third_party";

  const unitCache = new Map<string, RentUnit>();
  const unitFor = (
    apt: RentUnitApartment | null,
    prop: RentUnitProperty | null
  ): RentUnit => {
    const key = apt ? `apt:${apt.id}` : `prop:${prop?.id ?? "?"}`;
    const cached = unitCache.get(key);
    if (cached) return cached;

    const accountLessor =
      apt?.owner_user_id != null
        ? (lessorByOwnerUser[apt.owner_user_id] ?? null)
        : null;
    const lessorId = effectiveApartmentOwner(
      apt?.lessor_id ?? null,
      prop?.lessor_id ?? null,
      accountLessor
    );

    // Same inheritance + defaults as the generator's `resolved` CTE.
    const collectionMode: CollectionMode =
      (apt ? asMode(apt.collection_mode) : null) ??
      asMode(prop?.collection_mode) ??
      "company_collects";

    const propConfig: UnitFeeConfig = {
      revenue_model: asModel(prop?.revenue_model),
      coliviq_fee_pct: num(prop?.coliviq_fee_pct),
      coliviq_fee_amount: num(prop?.coliviq_fee_amount),
      master_rent: num(prop?.master_rent),
    };
    const aptConfig: UnitFeeConfig | null = apt
      ? {
          revenue_model: asModel(apt.revenue_model),
          coliviq_fee_pct: num(apt.coliviq_fee_pct),
          coliviq_fee_amount: num(apt.coliviq_fee_amount),
          master_rent: num(apt.master_rent),
        }
      : null;
    const feeConfig = resolveUnitFeeConfig(aptConfig, propConfig);
    // The generator falls back to full_rent when neither level names a model.
    if (feeConfig.revenue_model == null) feeConfig.revenue_model = "full_rent";

    const unit: RentUnit = {
      key,
      ownership: ownershipFor(lessorId),
      collectionMode,
      feeConfig,
    };
    unitCache.set(key, unit);
    return unit;
  };

  const byRoom = new Map<string, RentUnit>();
  for (const room of rooms) {
    const apt =
      room.apartment_id != null
        ? (aptById.get(String(room.apartment_id)) ?? null)
        : null;
    const propId = apt?.property_id ?? room.property_id;
    const prop = propId != null ? (propById.get(String(propId)) ?? null) : null;
    byRoom.set(String(room.id), unitFor(apt, prop));
  }
  return byRoom;
}

export type RentPaymentInput = {
  /** EUR. */
  amount: number;
  /** The period the payment belongs to (rent: its due month). */
  date: Date;
  /** Room the rent was paid for, or null when it could not be resolved. */
  roomId: Id | null | undefined;
};

export type RentRevenueSplit = {
  /**
   * Rent on CoLiviq-owned units — CoLiviq's own rent revenue. Includes rent
   * that resolves to no room at all (see `unresolvedRent`): dropping it would
   * silently lose revenue, and before this split it always counted here.
   */
  ownRent: number;
  /** Rent CoLiviq collected on company_collects third-party units (gross). */
  passThroughGross: number;
  /**
   * The fee CoLiviq keeps out of `passThroughGross` (per unit, per month) for
   * units that are NOT invoiced for it — recognised as management revenue the
   * moment the tenant's rent lands, because no document will ever say
   * otherwise — a month the generator has not billed yet, or a unit it skips
   * (zero fee, no occupancy, full_rent with no master rent on file).
   */
  retainedFees: number;
  /**
   * The same fee, for unit-months an OFFSET INVOICE already bills.
   *
   * Deducted from the payout exactly like `retainedFees` — the economics are
   * identical — but deliberately kept out of it, because where an invoice
   * exists the invoice is the system of record: the fee is recognised when
   * that invoice is marked paid, not when the tenant paid rent. Counting it in
   * both places would report the fee twice.
   */
  invoicedRetainedFees: number;
  /**
   * `passThroughGross − retainedFees − invoicedRetainedFees`: what is wired to
   * the owners. Both kinds of fee are withheld in reality, so both come off
   * here regardless of which one the dashboard has recognised yet.
   */
  passedOn: number;
  /** Rent on owner_collects third-party units — never a CoLiviq inflow. */
  ownerCollected: number;
  /** The part of `ownRent` that could not be attributed to a room. */
  unresolvedRent: number;
};

const EMPTY_SPLIT: RentRevenueSplit = {
  ownRent: 0,
  passThroughGross: 0,
  retainedFees: 0,
  invoicedRetainedFees: 0,
  passedOn: 0,
  ownerCollected: 0,
  unresolvedRent: 0,
};

const monthKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/**
 * Rent actually COLLECTED on a row, in EUR (COL-417).
 *
 * Revenue used to be "amount_cents of every effectively-paid row". Once a month
 * can be part-paid that understates what arrived: EUR 200 of a EUR 650 month is
 * real money in the account, and COL-417 requires the dashboard to move as
 * partials come in rather than only on full settlement.
 *
 * A SETTLED row still contributes its billed amount, not its received amount.
 * The two are equal in the normal case, and where they differ the row was
 * OVERPAID — that surplus is carried forward as credit against a future month,
 * so recognising it here would book next month's rent as this month's revenue.
 *
 * A row with nothing received contributes nothing, which is what every caller
 * did before this existed.
 */
export function collectedRentEUR(payment: {
  effective_status?: string | null;
  status?: string | null;
  amount_cents?: number | null;
  received_cents?: number | null;
}): number {
  const state = String(payment?.effective_status ?? payment?.status ?? "");
  if (state === "paid") return (Number(payment?.amount_cents) || 0) / 100;
  const received = Number(payment?.received_cents) || 0;
  return received > 0 ? received / 100 : 0;
}

/**
 * Split paid rent into CoLiviq's own rent, pass-through and owner-collected,
 * for the given display window.
 *
 * `payments` must be the FULL set of paid rent rows in scope (no date
 * filter) — not pre-filtered to `window` — so a unit's monthly fee is always
 * computed from its complete month, then allocated into `window` in
 * proportion to how much of that month's rent falls inside it. Passing an
 * already-window-filtered list would silently reapply the whole month's
 * master-rent deduction (or flat fee) to whatever partial rent happens to
 * fall in the window, instead of the window's fair share of it.
 */
export function splitRentRevenue(
  payments: readonly RentPaymentInput[],
  unitByRoom: ReadonlyMap<string, RentUnit>,
  window: { start: Date; end: Date },
  /**
   * `<unit key>|YYYY-MM` for every unit-month an offset invoice already bills
   * — from offsetInvoicedUnitMonths. Those fees are routed to
   * `invoicedRetainedFees` instead of `retainedFees`, so the dashboard
   * recognises them off the paid invoice rather than off the rent. Omit it and
   * every fee is treated as uninvoiced, which is the behaviour that predates
   * offset invoicing.
   */
  invoicedUnitMonths: ReadonlySet<string> = new Set()
): RentRevenueSplit {
  const out: RentRevenueSplit = { ...EMPTY_SPLIT };
  const inWindow = (d: Date) => d >= window.start && d <= window.end;

  // company_collects third-party rent, bucketed per unit per CALENDAR MONTH
  // (never per display window) so the per-room flat fee and the master-rent
  // deduction apply once per month, from the month's full rent — `windowRent`
  // tracks only the slice of that same bucket falling inside `window`, used
  // below to allocate the month's fee proportionally.
  const passThrough = new Map<
    string,
    {
      /** `<unit key>|YYYY-MM`, matched against invoicedUnitMonths below. */
      key: string;
      unit: RentUnit;
      rent: number;
      windowRent: number;
      rooms: Set<string>;
    }
  >();

  for (const p of payments) {
    const amount = Number(p.amount) || 0;
    const within = inWindow(p.date);
    const unit =
      p.roomId != null ? (unitByRoom.get(String(p.roomId)) ?? null) : null;
    if (!unit) {
      if (within) {
        out.ownRent += amount;
        out.unresolvedRent += amount;
      }
      continue;
    }
    if (unit.ownership === "coliviq") {
      if (within) out.ownRent += amount;
      continue;
    }
    if (unit.collectionMode === "owner_collects") {
      if (within) out.ownerCollected += amount;
      continue;
    }
    if (within) out.passThroughGross += amount;
    const key = `${unit.key}|${monthKey(p.date)}`;
    const bucket = passThrough.get(key) ?? {
      key,
      unit,
      rent: 0,
      windowRent: 0,
      rooms: new Set<string>(),
    };
    bucket.rent += amount;
    if (within) bucket.windowRent += amount;
    bucket.rooms.add(String(p.roomId));
    passThrough.set(key, bucket);
  }

  for (const b of passThrough.values()) {
    if (b.windowRent === 0) continue;
    const fee = unitMonthlyRevenue(b.unit.feeConfig, b.rooms.size, b.rent);
    // CoLiviq cannot keep more than it collected across the whole month (a
    // per-room flat fee on a partial month, say) — and never a negative fee.
    const monthFee = Math.min(Math.max(fee, 0), Math.max(b.rent, 0));
    // This window's proportional share of the month's fee, by how much of
    // the month's rent landed in it — never more than `windowRent` itself,
    // since monthFee <= b.rent.
    const share = monthFee * (b.windowRent / b.rent);
    if (invoicedUnitMonths.has(b.key)) out.invoicedRetainedFees += share;
    else out.retainedFees += share;
  }
  out.passedOn =
    out.passThroughGross - out.retainedFees - out.invoicedRetainedFees;
  return out;
}

/**
 * The unit-months an OFFSET management invoice already bills, as
 * `<unit key>|YYYY-MM` — the key `splitRentRevenue` buckets pass-through rent
 * under, so the two line up without either side knowing the other's shape.
 *
 * Feeds `splitRentRevenue`'s `invoicedUnitMonths`, which is what makes the
 * paid invoice the source of truth for management revenue WHERE ONE EXISTS,
 * without erasing the fee on the unit-months no invoice covers. Without this
 * pairing the
 * dashboard either counts an offset fee twice (once off the rent, once off the
 * paid invoice) or, if invoices alone are trusted, loses every fee that was
 * never documented.
 *
 * Only offset invoices are considered. A transfer invoice bills an
 * owner_collects unit, whose rent CoLiviq never collects and which therefore
 * contributes nothing to `retainedFees` to begin with.
 *
 * An UNPAID invoice still claims its unit-month: the fee it bills is
 * recognised when that invoice is marked paid, and leaving it in
 * `retainedFees` until then would recognise it twice over its life — once on
 * the rent, again on the invoice.
 *
 * A CANCELED invoice claims nothing. It will never be marked paid, so nothing
 * downstream can ever recognise its fee; if it still held its unit-month the
 * fee would be excluded from `retainedFees` AND absent from the paid-invoice
 * total, and simply vanish from Management revenue with no way to get it back.
 * Releasing the unit-month hands it to the rent-derived path, which is the
 * correct fallback — that is exactly the state a unit with no invoice is in.
 *
 * A consolidated invoice (one per property since 20260911120000) bills several
 * apartments at once, so every entry in fee_basis.properties[] is claimed, not
 * just the row's own apartment_id — which is NULL on exactly those invoices.
 */
export function offsetInvoicedUnitMonths(
  invoices: readonly {
    settlement?: string | null;
    status?: string | null;
    apartment_id?: number | null;
    property_id?: number | null;
    period_month?: string | null;
    fee_basis?: Json | null;
  }[]
): Set<string> {
  const out = new Set<string>();
  for (const inv of invoices) {
    if (inv.settlement !== "offset") continue;
    if (inv.status === "canceled") continue;
    const month = String(inv.period_month ?? "").slice(0, 7);
    if (month.length !== 7) continue;
    const itemised =
      inv.fee_basis && typeof inv.fee_basis === "object"
        ? (inv.fee_basis as { properties?: unknown }).properties
        : undefined;
    const entries = Array.isArray(itemised)
      ? (itemised as Array<{
          apartment_id?: number | string | null;
          property_id?: number | string | null;
        }>)
      : [];
    // No itemisation (a hand-created or pre-consolidation row) — fall back to
    // the invoice's own ids, which is the single unit it can only be billing.
    const units = entries.length
      ? entries
      : [{ apartment_id: inv.apartment_id, property_id: inv.property_id }];
    for (const u of units) {
      const key =
        u.apartment_id != null
          ? `apt:${u.apartment_id}`
          : u.property_id != null
            ? `prop:${u.property_id}`
            : null;
      if (key) out.add(`${key}|${month}`);
    }
  }
  return out;
}

/**
 * Places a value from the database on the analytics timeline (COL-630).
 *
 * A date-only value (`period_month`, `due_date`) has no time of day, so it is
 * anchored at local noon — safely inside its calendar day. A timestamp
 * (`paid_at`, `created_at`) is an instant and stays one: cutting it to its UTC
 * date would move a payout confirmed between local midnight and the UTC
 * offset onto the previous day.
 *
 * Returns null for a missing or unparseable value.
 */
export function analyticsDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? new Date(`${s}T12:00:00`)
    : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
